import store from "/WebInterface/new-ui/upload/js/reducer.js";
import WSUploads from "/WebInterface/new-ui/upload/js/ws.js"; // This will do WS uploads
import XHRUploads from "/WebInterface/new-ui/upload/js/xhr.js"; // This will do XHR uploads
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import { handleFileOpenError } from "/WebInterface/new-ui/upload/js/common.js";

class UploadingQueue {
  constructor() {
    this.queue = [];
    this.fileUploadingInProgress = false;
    this.currentUploadingFileInstance = null;
    this.currentFile = null;
    this.completedFilesFromQueue = [];
    this.globalCancelEnabled = false;
  }

  getQueue = () => {
    return this.queue;
  };

  setQueue = (newQueue) => {
    this.queue = newQueue;
    this.onQueueUpdate(); // Call the method when queue updates
  };

  onQueueUpdate() {
    const queue = [...this.queue];
    this.invoke("setUploadingStatus", [
      "isFilesOnUploadQueue",
      queue.length !== 0,
    ]);
  }

  toggleUploadType = (file) => {
    store.dispatch("updateFileStatusAndRerenderFile", {
      [file.id]: {
        ...file,
        uploadType: file.uploadType === "WS" ? "Normal" : "WS",
      },
    });
  };

  addFileToUploadingQueue = (file) => {
    let queue = [...this.queue];
    const isDownloadInProgress = this.invoke("getDownloadStatus");

    if (isDownloadInProgress) {
      _$("#uploadDeniedInfo").removeClass("is-hidden");
    }

    store.dispatch("updateFileStatusAndRerenderFile", {
      [file.id]: {
        ...file,
        status: "queued",
      },
    });

    queue.push(file);
    this.setQueue(queue);

    if (queue.length === 1 && !isDownloadInProgress) {
      this.startUploadingCurrentFile(file);
    }

    store.dispatch("enableGlobalUpload");
  };

  continueUpload() {
    const queue = [...this.queue];
    _$("#uploadDeniedInfo").addClass("is-hidden");

    if (queue.length === 0) {
      return;
    }
    const file = queue[0];
    this.startUploadingCurrentFile(file);
  }

  nextFileInQueue = (canceled) => {
    let queue = [...this.queue];
    const done_file = queue.shift();
    this.completedFilesFromQueue.push(done_file);
    this.setQueue(queue);

    if (this.globalCancelEnabled) {
      this.queue.forEach((file) => {
        const { id: fileId } = file;

        store.dispatch("updateFileStatusAndRerenderFile", {
          [fileId]: {
            ...file,
            status: "canceled",
          },
        });
      });
      queue = [];
      this.setQueue([]);
      this.globalCancel = false;
    }

    if (queue.length !== 0) {
      const file = queue[0];

      this.startUploadingCurrentFile(file);
      return;
    }

    this.handleUpdateUploadingStatus(false);
    store.dispatch("fileUploadingQueueFinished", {
      canceled,
      completedFiles: this.completedFilesFromQueue,
    });
    store.dispatch("disableGlobalUpload");
    this.completedFilesFromQueue = [];
  };

  stopFileUploadingQueueOnQuotaStorageExceeded = (message) => {
    const queue = [...this.queue];
    clearInterval(this.chunksInfo?.intervalId);

    queue.forEach((file) => {
      handleFileOpenError(file, message || t("failedStorageQuotaExceeded"));
    });

    this.setQueue([]);
    store.dispatch("fileUploadingQueueFinished", {
      canceled: true,
      completedFiles: this.completedFilesFromQueue,
    });
  };

  removeFileFromUploadingQueue = (file) => {
    const id = file?.id;

    if (!id) {
      return;
    }

    let queue = [...this.queue];
    queue = queue.filter(
      (file) => file?.id !== id && this?.currentFile?.id !== id
    );

    this.setQueue(queue);
    store.dispatch("enableGlobalUpload");
  };

  handleUpdateUploadingStatus(uploading) {
    if (
      (uploading === true || uploading === false) &&
      uploading !== this.fileUploadingInProgress
    ) {
      this.fileUploadingInProgress = uploading;
      this.invoke("setUploadingStatus", [
        "isUploading",
        this.fileUploadingInProgress,
      ]);
    }

    return this.fileUploadingInProgress;
  }

  invoke = (method, params) => {
    if (parent.window[method]) {
      return parent.window[method].apply(this, params);
    }
  };

  getUploadTypeCustomizations = () => {
    const maxRetries = this.invoke("getUserCustomizationValue", [
      "maxRetriesBeforeCancellingUpload",
    ]);

    const maxChunkSize = this.invoke("getUserCustomizationValue", [
      "upload_MaxChunkSizeInMB",
    ]);

    const altDomains = this.invoke("getAlternateDomains");

    const serverWriteThresholdWSUpload = this.invoke(
      "getUserCustomizationValue",
      ["serverWriteThresholdWSUpload"]
    );

    const WSUploadMaxRequests = this.invoke("getUserCustomizationValue", [
      "WSUploadMaxRequests",
    ]);

    const WSUploadDefaultChunkSize = this.invoke("getUserCustomizationValue", [
      "WSUploadDefaultChunkSize",
    ]);

    const showServerWriteProgress = this.invoke("getUserCustomizationValue", [
      "showServerWriteProgress",
    ]);

    return {
      maxRetries,
      maxChunkSize,
      altDomains,
      serverWriteThresholdWSUpload,
      WSUploadMaxRequests,
      WSUploadDefaultChunkSize,
      showServerWriteProgress,
    };
  };

  startUploadingCurrentFile(file) {
    this.currentFile = file;
    const { uploadType } = file;
    const {
      maxRetries,
      maxChunkSize,
      altDomains,
      serverWriteThresholdWSUpload,
      WSUploadMaxRequests,
      WSUploadDefaultChunkSize,
      showServerWriteProgress,
    } = this.getUploadTypeCustomizations();

    this.currentUploadingFileInstance =
      uploadType === "WS"
        ? new WSUploads({
            maxRetries,
            serverWriteThresholdWSUpload,
            WSUploadMaxRequests,
            WSUploadDefaultChunkSize,
            showServerWriteProgress,
          })
        : new XHRUploads({
            maxRetries,
            maxChunkSize,
            altDomains,
          });

    store.dispatch("scrollToCurrentUploadFile", { file });
    store.dispatch("enableGlobalUpload");
    this.currentUploadingFileInstance.openFileStream(file);
  }

  pausing() {
    this.currentUploadingFileInstance.pausing();
  }

  resume() {
    this.currentUploadingFileInstance.resume();
  }

  cancel(cancelAll = false) {
    this.globalCancelEnabled = cancelAll;

    if (
      this.currentFile.status !== "canceling" ||
      this.currentFile.status !== "canceled"
    ) {
      this.currentUploadingFileInstance.cancel();
    }
  }

  getGlobalUploadedFiles() {
    return [...this.completedFilesFromQueue, ...this.queue];
  }

  getCurrentUploadingFile() {
    return this.currentFile;
  }

  getCompletedFiles() {
    return this.completedFilesFromQueue;
  }

  setElapsedTime(duration = 1) {
    this.elapsedTime = duration;
  }

  setBytesUploaded(bytes = 1) {
    this.bytesUploaded = bytes;
  }

  setTotalBytesToUpload(bytes = 1) {
    this.totalBytesToUpload = bytes;
  }

  getElapsedTime() {
    return this.elapsedTime;
  }

  getBytesUploaded() {
    return this.bytesUploaded;
  }

  getTotalBytesToUpload() {
    return this.totalBytesToUpload;
  }
}

const uploadingQueue = new UploadingQueue();
export default uploadingQueue;
